import React from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAccessibility } from './AccessibilityProvider';
import { Eye, Type, Zap, Contrast } from 'lucide-react';

const AccessibilitySettings = () => {
  const {
    highContrast,
    setHighContrast,
    reducedMotion,
    setReducedMotion,
    fontSize,
    setFontSize
  } = useAccessibility();

  const fontSizeOptions = [
    { value: 'small', label: 'Small', description: '14px base size' },
    { value: 'normal', label: 'Normal', description: '16px base size' },
    { value: 'large', label: 'Large', description: '18px base size' },
    { value: 'extra-large', label: 'Extra Large', description: '20px base size' }
  ];

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Eye className="w-6 h-6 text-yellow-600" />
          <div>
            <h3 className="text-lg font-semibold">Accessibility Settings</h3>
            <p className="text-sm text-gray-600">Customize the interface for better accessibility</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* High Contrast Mode */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="high-contrast" className="flex items-center space-x-2">
                <Contrast className="w-4 h-4" />
                <span>High Contrast Mode</span>
              </Label>
              <p className="text-sm text-gray-600">
                Increases contrast between text and background for better readability
              </p>
            </div>
            <Switch
              id="high-contrast"
              checked={highContrast}
              onCheckedChange={setHighContrast}
              aria-describedby="high-contrast-description"
            />
          </div>

          {/* Reduced Motion */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="reduced-motion" className="flex items-center space-x-2">
                <Zap className="w-4 h-4" />
                <span>Reduce Motion</span>
              </Label>
              <p className="text-sm text-gray-600">
                Minimizes animations and transitions that may cause discomfort
              </p>
            </div>
            <Switch
              id="reduced-motion"
              checked={reducedMotion}
              onCheckedChange={setReducedMotion}
              aria-describedby="reduced-motion-description"
            />
          </div>

          {/* Font Size */}
          <div className="space-y-3">
            <Label className="flex items-center space-x-2">
              <Type className="w-4 h-4" />
              <span>Font Size</span>
            </Label>
            <Select value={fontSize} onValueChange={setFontSize}>
              <SelectTrigger className="w-full" aria-label="Select font size">
                <SelectValue placeholder="Select font size" />
              </SelectTrigger>
              <SelectContent>
                {fontSizeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div>
                      <div className="font-medium">{option.label}</div>
                      <div className="text-xs text-gray-500">{option.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reset to Defaults */}
          <div className="pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setHighContrast(false);
                setReducedMotion(false);
                setFontSize('normal');
              }}
              className="w-full"
            >
              Reset to Defaults
            </Button>
          </div>
        </div>
      </Card>

      {/* Keyboard Navigation Help */}
      <Card className="p-6">
        <h4 className="font-semibold mb-4">Keyboard Navigation</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="font-medium">Tab</span>
            <span className="text-gray-600">Navigate forward</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium">Shift + Tab</span>
            <span className="text-gray-600">Navigate backward</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium">Enter / Space</span>
            <span className="text-gray-600">Activate button or link</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium">Escape</span>
            <span className="text-gray-600">Close modal or menu</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium">Arrow Keys</span>
            <span className="text-gray-600">Navigate within menus</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AccessibilitySettings;